import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { dashboardFor } from "@/lib/roleRoute";
import { Mail, Lock, Eye, EyeOff, Users, ShieldCheck, Cloud, Shield, CalendarPlus, ArrowLeft, Building2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { applyBranding } from "@/lib/branding";

function isMobileDevice() {
  if (typeof window === "undefined") return false;
  return /android|iphone|ipad|ipod/i.test(window.navigator.userAgent || "") || window.innerWidth < 768;
}

function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

// Mirrors RootRedirect in App.js. Login navigates straight here rather than
// bouncing through "/", so this has to stay in step with it — including the
// dental branch, which wins over the phone-specific views (the dental space is
// tablet-first already).
function landingFor(user) {
  const role = user?.role;
  if (!role) return null;
  const clinicType = user?.clinic_type || "medical";
  if (clinicType === "dental") {
    return dashboardFor({ role, clinicType });
  }
  if (role === "doctor" && (isStandalonePWA() || isMobileDevice())) {
    return "/doctor-mobile";
  }
  if (role === "reception" && (isStandalonePWA() || isMobileDevice())) {
    return "/reception-mobile";
  }
  return dashboardFor({ role, clinicType });
}

// SINGLE SOURCE OF TRUTH for login.
// - Calls AuthContext.login() (POSTs to /api/auth/login via the api client)
// - AuthContext persists rxp_token + rxp_user
// Verified against POST /api/auth/login on 2026-09-06 — every one returns a token.
const DEMO_ACCOUNTS = [
  { role: "Platform Admin", email: "admin@aruga.msr.asia", password: "Admin@123" },
  { role: "Clinic Admin", email: "doctor@rxprint.com", password: "admin123" },
  { role: "Hospital Admin", email: "admin@stluke.demo", password: "admin123" },
  { role: "Doctor", email: "doctor@stluke.demo", password: "doctor123" },
  { role: "Reception", email: "reception@stluke.demo", password: "reception123" },
  { role: "Staff", email: "staff@stluke.demo", password: "staff123" },
];

// - Strict role -> dashboard mapping. No substring matches. No guessing.
export default function Login() {
  const { user, loading, login, selectHospital } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [hospitalBrand, setHospitalBrand] = useState(null);

  // Multi-hospital picker state
  const [picker, setPicker] = useState(null); // { memberships, pendingToken, user }
  const [pickingId, setPickingId] = useState(""); // hospital_id currently being picked
  const [quick, setQuick] = useState(""); // email of the one-click demo login in flight

  // If we know the user's last hospital, load its branding to theme the login screen
  useEffect(() => {
    const hid = localStorage.getItem("rxp_last_hospital");
    if (!hid) return;
    api.get(`/public/hospitals/${hid}/branding`)
      .then(({ data }) => {
        if (data?.primary_color || data?.logo_url) {
          setHospitalBrand(data);
          applyBranding(data);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && user) {
      const dest = landingFor(user);
      if (dest) navigate(dest, { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim() || !password) {
      setErr("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await login(email.trim(), password);
      if (res?.kind === "select_hospital") {
        // Show in-page picker — DO NOT navigate yet.
        setPicker({
          memberships: res.memberships || [],
          pendingToken: res.pendingToken,
          user: res.user,
        });
        return;
      }
      const role = res?.user?.role;
      const dest = landingFor(res?.user);
      if (!dest) {
        setErr(`Your role "${role || "unknown"}" has no assigned dashboard. Contact your administrator.`);
        return;
      }
      navigate(dest, { replace: true });
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  // One-click demo login: authenticate and land on that role's dashboard in one click.
  const oneClick = async (d) => {
    setErr("");
    setEmail(d.email);
    setPassword(d.password);
    setQuick(d.email);
    try {
      const res = await login(d.email, d.password);
      if (res?.kind === "select_hospital") {
        setPicker({ memberships: res.memberships || [], pendingToken: res.pendingToken, user: res.user });
        return;
      }
      const dest = landingFor(res?.user);
      if (!dest) {
        setErr(`Your role "${res?.user?.role || "unknown"}" has no assigned dashboard.`);
        return;
      }
      navigate(dest, { replace: true });
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Invalid email or password.");
    } finally {
      setQuick("");
    }
  };

  const onGoogle = () => {
    toast.info("Sign in with Google is not enabled yet.");
  };

  const pickHospital = async (m) => {
    if (!picker?.pendingToken) return;
    setPickingId(m.hospital_id);
    setErr("");
    try {
      const u = await selectHospital(picker.pendingToken, m.hospital_id);
      const dest = landingFor(u);
      if (!dest) {
        setErr(`Your role "${u?.role || "unknown"}" has no assigned dashboard.`);
        setPickingId("");
        return;
      }
      navigate(dest, { replace: true });
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Could not sign in to that hospital.");
      setPickingId("");
    }
  };

  const cancelPicker = () => {
    setPicker(null);
    setPickingId("");
    setErr("");
  };

  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="login-page">
      {/* ============ LEFT PANEL ============ */}
      <aside
        className="hidden md:flex relative flex-col justify-between text-white p-12 lg:p-16 w-1/2 overflow-hidden"
        style={
          hospitalBrand?.primary_color
            ? { background: `linear-gradient(135deg, ${hospitalBrand.primary_color} 0%, ${hospitalBrand.accent_color || hospitalBrand.primary_color} 100%)` }
            : { background: "linear-gradient(135deg, #0f2a63 0%, #17357e 30%, #1f4c9e 65%, #2c63b5 100%)" }
        }
      >
        {/* Brand */}
        <div>
          {hospitalBrand?.logo_url ? (
            <div className="flex items-center gap-3">
              <img src={hospitalBrand.logo_url} alt="" className="h-14 w-14 rounded-xl bg-white/20 p-1.5 object-contain" />
              <div>
                <div className="text-2xl lg:text-3xl font-extrabold leading-tight" style={{ fontFamily: "Work Sans" }}>
                  {hospitalBrand?.hospital_name || "Welcome back"}
                </div>
                <div className="mt-1 tracking-[0.32em] text-[10px] lg:text-xs text-white/80 font-medium">
                  Powered by MSR ALAGA
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              <img
                src="/brand/msr-alaga-logo.png"
                alt="MSR ALAGA — All-in-One Lifecycle Administration for General Healthcare Applications"
                className="w-[320px] lg:w-[400px] max-w-full h-auto rounded-xl bg-white p-4 shadow-lg"
              />
            </div>
          )}
        </div>

        {/* Headline + copy */}
        <div className="max-w-md">
          <h1
            className="text-4xl lg:text-5xl font-extrabold leading-tight"
            style={{ fontFamily: "Work Sans" }}
          >
            <span className="whitespace-nowrap">Whenever. Wherever.</span>
            <br />
            <span className="whitespace-nowrap" style={{ color: "#8FD167" }}>Save time. Save lives.</span>
          </h1>
          <div className="h-px bg-white/30 my-6 w-16" />
          <p className="text-white/85 text-base lg:text-lg leading-relaxed">
            All-in-one lifecycle administration for general healthcare applications — for clinics,
            hospitals, and healthcare providers.
          </p>
        </div>

        {/* Feature pills */}
        <div className="grid grid-cols-4 gap-3 lg:gap-4 max-w-xl">
          <Feature icon={<Users size={20} />} label={["Multi-tenant", "by design"]} />
          <Feature icon={<Shield size={20} />} label={["Strict data", "isolation"]} />
          <Feature icon={<Cloud size={20} />} label={["Always", "accessible"]} />
          <Feature icon={<ShieldCheck size={20} />} label={["Secure &", "compliant"]} />
        </div>

        {/* Set Appointment CTA (desktop, left panel) */}
        <Link
          to="/book"
          data-testid="login-set-appointment"
          className="relative z-10 inline-flex items-center justify-center gap-2 w-fit mt-2 px-5 py-3 rounded-xl bg-white text-[#17357E] font-semibold text-sm shadow-md hover:bg-[#eef3fb] transition self-start"
        >
          <CalendarPlus size={18} />
          <span>Set Appointment</span>
        </Link>

        {/* Heartbeat line bottom */}
        <svg
          aria-hidden="true"
          className="absolute bottom-2 left-0 right-0 w-full"
          style={{ color: "rgba(143, 209, 103, 0.75)" }}
          height="38"
          viewBox="0 0 1200 60"
          preserveAspectRatio="none"
        >
          <path
            d="M0 30 L260 30 L290 14 L320 46 L350 8 L380 52 L410 26 L440 34 L470 30 L760 30 L790 14 L820 46 L850 12 L880 48 L910 30 L1200 30"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      </aside>

      {/* ============ RIGHT PANEL ============ */}
      <main className="flex-1 flex items-center justify-center px-6 py-10 md:px-10 lg:px-16">
        <div className="w-full max-w-md">
          {/* Mobile brand (only on small screens) */}
          <div className="md:hidden mb-8">
            <img
              src="/brand/msr-alaga-logo.png"
              alt="MSR ALAGA — All-in-One Lifecycle Administration for General Healthcare Applications"
              className="w-full max-w-[300px] h-auto"
            />
          </div>

          {/* SIGN IN eyebrow */}
          <div className="mb-5">
            <span className="text-xs font-semibold tracking-[0.18em] text-[#17357E] uppercase">
              {picker ? "Choose hospital" : "Sign in"}
            </span>
            <div className="h-[3px] w-9 bg-[#17357E] rounded-full mt-1" />
          </div>

          {picker ? (
            <>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight"
                style={{ fontFamily: "Work Sans" }}
              >
                Hi, {picker.user?.name || picker.user?.email?.split("@")[0] || "there"}.
              </h2>
              <p className="text-slate-500 mt-2 mb-6">
                You are assigned to multiple hospitals. Pick one to sign in to.
              </p>

              <div className="space-y-3" data-testid="hospital-picker">
                {(picker.memberships || []).map((m) => {
                  const busy = pickingId === m.hospital_id;
                  return (
                    <button
                      key={m.hospital_id}
                      type="button"
                      onClick={() => pickHospital(m)}
                      disabled={!!pickingId}
                      className="w-full text-left flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition disabled:opacity-60"
                      data-testid={`pick-hospital-${m.hospital_id}`}
                    >
                      <div className="h-11 w-11 rounded-xl bg-[#e7eefb] text-[#17357E] flex items-center justify-center shrink-0">
                        <Building2 size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{m.hospital_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-wider">{m.role}</div>
                      </div>
                      {busy ? (
                        <span className="text-xs text-[#17357E] font-medium">Signing in…</span>
                      ) : (
                        <ArrowRight size={18} className="text-slate-400" />
                      )}
                    </button>
                  );
                })}
              </div>

              {err ? (
                <div
                  className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                  data-testid="login-error"
                >
                  {err}
                </div>
              ) : null}

              <button
                type="button"
                onClick={cancelPicker}
                disabled={!!pickingId}
                className="mt-5 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 disabled:opacity-60"
                data-testid="hospital-picker-back"
              >
                <ArrowLeft size={14} /> Use a different account
              </button>
            </>
          ) : (
            <>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight"
                style={{ fontFamily: "Work Sans" }}
              >
                Welcome back.
              </h2>
              <p className="text-slate-500 mt-2 mb-8">Enter your credentials to continue.</p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {/* Email */}
            <div>
              <label
                htmlFor="login-email"
                className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase"
              >
                Email
              </label>
              <div className="mt-2 relative">
                <Mail
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@hospital.com"
                  className="w-full h-12 pl-10 pr-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  data-testid="login-email"
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="login-password"
                className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase"
              >
                Password
              </label>
              <div className="mt-2 relative">
                <Lock
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-12 pl-10 pr-11 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  data-testid="login-password"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 rounded-md"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {err ? (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                data-testid="login-error"
              >
                {err}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-xl bg-[#17357E] hover:bg-[#0f2a63] text-white font-semibold text-base shadow-sm transition disabled:opacity-60"
              data-testid="login-submit"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>

            {/* Mobile-only: Set Appointment button under Sign In */}
            <Link
              to="/book"
              data-testid="login-set-appointment-mobile"
              className="md:hidden w-full h-12 rounded-xl border-2 border-[#17357E] bg-white hover:bg-[#eef3fb] text-[#17357E] font-semibold text-base flex items-center justify-center gap-2 transition"
            >
              <CalendarPlus size={18} />
              <span>Set Appointment</span>
            </Link>

            {/* OR divider */}
            <div className="flex items-center gap-3 pt-1">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs font-medium text-slate-400 tracking-widest">OR</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Google */}
            <button
              type="button"
              onClick={onGoogle}
              className="w-full h-12 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-medium flex items-center justify-center gap-3 transition"
              data-testid="login-google"
            >
              <GoogleGlyph />
              <span>Sign in with Google</span>
            </button>
          </form>

            {/* One-click demo login */}
            <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="demo-accounts">
              <div className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase mb-3">
                One-click demo login
              </div>
              <div className="grid grid-cols-2 gap-2">
                {DEMO_ACCOUNTS.map((d) => (
                  <button
                    key={d.email}
                    type="button"
                    onClick={() => oneClick(d)}
                    disabled={!!quick || submitting}
                    data-testid={`demo-login-${d.role.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-[#17357E]/40 hover:bg-[#eef3fb] disabled:opacity-60"
                  >
                    <span className="block text-[11px] font-semibold text-slate-800">{d.role}</span>
                    <span className="block truncate text-[10px] text-slate-500">
                      {quick === d.email ? "Signing in…" : d.email}
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-3 text-[10px] text-slate-500">
                One click signs you straight in as that role. No password to type.
              </div>
            </div>
            </>
          )}

          {/* Footer */}
          <div className="mt-10 text-center text-xs text-slate-400 leading-relaxed">
            <div className="mb-3 mx-auto max-w-sm text-[11px] text-slate-500 leading-relaxed" data-testid="security-badge">
              🔒 Secured with encryption in transit (HTTPS/TLS), role-based access, and audit logging — built in line with the Data Privacy Act (RA 10173).
            </div>
            <div>© {new Date().getFullYear()} MSR ALAGA Healthcare Platform</div>
            <div>All rights reserved.</div>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ---------- helpers ---------- */

function Feature({ icon, label }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="h-12 w-12 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white">
        {icon}
      </div>
      <div className="mt-2 text-[11px] lg:text-xs font-medium text-white/90 leading-tight">
        {label[0]}
        <br />
        {label[1]}
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}
